import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { useState } from 'react'
import { isTaskDone } from '@/entities/board'
import {
  type CustomerBoardColumn,
  type CustomerTask,
  customerBoardLanes,
  customerBoardQueryOptions,
} from '@/entities/task'
import { CreateCustomerTaskDialog } from '@/features/create-customer-task'
import { ApiError } from '@/shared/api'
import { Button } from '@/shared/ui/button'
import { DueDate } from '@/shared/ui/due-date'
import { Icon } from '@/shared/ui/icon'
import { PriorityChip } from '@/shared/ui/priority-chip'
import { Spinner } from '@/shared/ui/spinner'
import { StatusScreen } from '@/shared/ui/status-screen'

export const Route = createFileRoute('/my/boards_/$boardId')({
  head: () => ({ meta: [{ title: 'Доска · STIT Task' }] }),
  component: CustomerBoardPage,
})

// Канбан заказчика: те же этапы, что у команды, и только открытые ему карточки.
// Отдельный компонент, а не BoardView сотрудника: тот вшит в мутации, DnD и
// таймеры — «выключить» их значило бы притащить весь их код в витрину клиента.
function CustomerBoardPage() {
  const { boardId } = Route.useParams()
  const boardQ = useQuery(customerBoardQueryOptions(boardId))
  // undefined — диалог закрыт; '' — открыт без колонки (ляжет в первую).
  const [createIn, setCreateIn] = useState<string>()

  if (boardQ.isPending) {
    return (
      <div className="container-x px-4 py-8 sm:px-6 sm:py-10">
        <Spinner label="Загрузка доски" />
      </div>
    )
  }

  if (boardQ.isError) {
    // Закрытая или чужая доска отвечает 404 — существование не раскрываем.
    const notFound = boardQ.error instanceof ApiError && boardQ.error.status === 404
    return (
      <StatusScreen
        title={notFound ? 'Доска недоступна' : 'Не удалось загрузить доску'}
        message={
          notFound
            ? 'Возможно, её ещё не открыли вам или она больше не актуальна.'
            : 'Попробуйте обновить страницу.'
        }
      >
        {!notFound && (
          <Button
            variant="secondary"
            onClick={() => {
              void boardQ.refetch()
            }}
          >
            Повторить
          </Button>
        )}
        <Link
          to="/my/projects"
          className="inline-flex items-center rounded-control px-5 py-3 text-sm text-text-secondary transition-colors hover:text-text-primary"
        >
          К моим проектам
        </Link>
      </StatusScreen>
    )
  }

  const board = boardQ.data
  const lanes = customerBoardLanes(board)

  return (
    <div className="flex h-full min-h-0 flex-col px-4 py-8 sm:px-6 sm:py-10">
      <nav
        aria-label="Хлебные крошки"
        className="mb-2 flex flex-wrap items-center gap-2 text-sm text-text-tertiary"
      >
        <Link to="/my/projects" className="transition-colors hover:text-text-primary">
          Мои проекты
        </Link>
        {board.projectId && (
          <>
            <span aria-hidden>·</span>
            <Link
              to="/my/projects/$projectId"
              params={{ projectId: board.projectId }}
              className="transition-colors hover:text-text-primary"
            >
              {board.projectName || 'Проект'}
            </Link>
          </>
        )}
      </nav>

      <div className="mb-6 flex items-center gap-3">
        <h1 className="t-h2 min-w-0 flex-1 truncate text-text-primary">{board.name}</h1>
        {board.columns.length > 0 && (
          <Button size="sm" onClick={() => setCreateIn('')}>
            <Icon name="plus" size={14} />
            Задача
          </Button>
        )}
      </div>

      {/* snap-x: на телефоне доска листается по колонке, а не останавливается на
          полпути между этапами. */}
      <div className="flex min-h-0 flex-1 snap-x snap-mandatory gap-4 overflow-x-auto pb-4 sm:snap-none">
        {lanes.map((lane) => (
          <CustomerLane
            key={lane.column.id}
            column={lane.column}
            tasks={lane.tasks}
            columns={board.columns}
            onCreate={() => setCreateIn(lane.column.id)}
          />
        ))}
      </div>
      {createIn !== undefined && (
        <CreateCustomerTaskDialog
          boardId={board.id}
          columns={board.columns}
          {...(createIn ? { defaultColumnId: createIn } : {})}
          onClose={() => setCreateIn(undefined)}
        />
      )}
    </div>
  )
}

function CustomerLane({
  column,
  tasks,
  columns,
  onCreate,
}: {
  column: CustomerBoardColumn
  tasks: CustomerTask[]
  columns: CustomerBoardColumn[]
  onCreate: () => void
}) {
  return (
    <section
      aria-label={column.name}
      className="flex h-full w-[86vw] shrink-0 snap-start flex-col sm:w-[300px] sm:snap-align-none"
    >
      <header className="mb-2 flex items-center gap-1.5 border-b border-border-subtle px-1 pb-2">
        <h2 className="min-w-0 flex-1 truncate text-sm font-medium text-text-primary">
          {column.name}
        </h2>
        <span data-numeric className="mono-num shrink-0 text-xs text-text-tertiary">
          {tasks.length}
        </span>
        {column.done && (
          <span
            role="img"
            aria-label="Этап завершения"
            title="Этап завершения"
            className="inline-flex shrink-0 text-success"
          >
            <Icon name="check" size={13} />
          </span>
        )}
        <button
          type="button"
          onClick={onCreate}
          aria-label={`Новая задача в «${column.name}»`}
          title="Новая задача"
          className="inline-flex shrink-0 rounded-control p-1 text-text-tertiary transition-colors hover:text-text-primary"
        >
          <Icon name="plus" size={13} />
        </button>
      </header>
      <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto overscroll-contain rounded-card bg-kanban-column p-2 ring-1 ring-border-subtle ring-inset">
        {tasks.length === 0 ? (
          <p className="rounded-card border border-dashed border-border-subtle px-4 py-8 text-center text-xs text-text-tertiary">
            здесь пока пусто
          </p>
        ) : (
          tasks.map((task) => <CustomerCard key={task.id} task={task} columns={columns} />)
        )}
      </div>
    </section>
  )
}

// Карточка витрины: читать и перейти. Ни ручки перетаскивания, ни меню, ни кнопок
// — клиент здесь читатель, и «неактивных» действий у него тоже нет.
function CustomerCard({ task, columns }: { task: CustomerTask; columns: CustomerBoardColumn[] }) {
  return (
    <article className="rounded-control bg-bg-surface p-3 ring-1 ring-border-subtle ring-inset transition-shadow hover:shadow-[0_8px_24px_rgba(0,0,0,0.35)]">
      <Link to="/my/tasks/$taskId" params={{ taskId: task.id }} className="group/title block">
        <div className="flex items-start gap-2">
          {task.type === 'bug' && (
            <span
              role="img"
              aria-label="Баг"
              title="Баг"
              className="mt-0.5 inline-flex shrink-0 text-text-tertiary"
            >
              <Icon name="bug" size={14} />
            </span>
          )}
          <h3 className="line-clamp-2 text-sm text-text-primary transition-colors group-hover/title:text-accent">
            {task.title}
          </h3>
        </div>
      </Link>

      {(task.priority !== null || task.dueDate) && (
        <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-text-tertiary">
          {task.priority !== null && <PriorityChip priority={task.priority} />}
          {task.dueDate && (
            // Закрытая работа не бывает просроченной: в done-колонке срок молчит.
            <DueDate dueDate={task.dueDate} muted={isTaskDone(task, columns)} />
          )}
        </div>
      )}
    </article>
  )
}
